import SearchIcon from '@mui/icons-material/Search';
import { FormControl, InputAdornment, InputLabel, ListSubheader, MenuItem, Select, SelectProps, SxProps, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import scssVariables from '../../../../Config.module.scss';
import languageService from '../../../Services/Language/LanguageService';
import { translationKeys } from '../../../Services/Language/TranslationDefinitions';
import { EventActionsEnum, EventCategoriesEnum } from '../../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../../Services/Logging/LoggingService';
import trackingService, { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import styles from './Inputs.module.scss';
import type { SelectInputOption } from './SelectInput';

interface IMuiDropdownProps<T> {
    className?: string | undefined;
    disabled?: boolean;
    placeholder?: string;
    value?: T;
    options: SelectInputOption<T>[];
    handleOptionChange: (value: T) => void;
    customStyles?: SxProps;
    label?: string | JSX.Element;
    isSearchable?: boolean;
    testId?: string;
    track: TrackingEvent;
}

const menuNavigationKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'];

const StyledMuiSelect = styled(Select)<SelectProps>(() => ({
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: `${scssVariables.inputActiveState} !important`,
    },
}));

// eslint-disable-next-line @typescript-eslint/comma-dangle
const Dropdown = <T,>(props: IMuiDropdownProps<T>): JSX.Element => {
    // Material UI does not allow non-string values, this component bridges that gap and allows it.
    const [searchText, setSearchText] = useState<string>('');

    const handleOptionChange = (label: string): void => {
        const option = props.options.find((x) => x.label === label);

        if (option?.disabled === true) {
            return;
        }

        if (option) {
            trackingService.track({ ...props.track, eventValue: option.label });

            loggingService.trackEventWithAnalytics(
                EventActionsEnum.DropdownSelection,
                EventCategoriesEnum.DropdownSelected,
                `${option.value}`,
                `${props.label}`,
            );
            props.handleOptionChange(option.value);
        }
    };

    return (
        <div data-testid={props.testId} className={styles.input_dropdown_flex}>
            {props.label && <p className={styles.input_dropdown_label}>{props.label}</p>}
            <FormControl className={props.className} fullWidth sx={{ m: 1, margin: 0 }} size="small">
                {props.placeholder && <InputLabel shrink={false}>{props.placeholder}</InputLabel>}
                <StyledMuiSelect
                    disabled={props.disabled}
                    className={`${styles.input_base_dropdown}`}
                    value={props.options.find((x) => x.value === props.value)?.label ?? ''}
                    onChange={(event) => handleOptionChange(event.target.value as string)}
                    onOpen={() => {
                        loggingService.trackEventWithAnalytics(
                            EventActionsEnum.DropdownSelection,
                            EventCategoriesEnum.DropdownOpened,
                            `${props.value}`,
                            `${props.label}`,
                        );
                    }}
                    onClose={() => {
                        setSearchText('');
                        loggingService.trackEventWithAnalytics(EventActionsEnum.DropdownSelection, EventCategoriesEnum.DropdownClosed, '', `${props.label}`);
                    }}
                    sx={{
                        ...{
                            backgroundColor: scssVariables.plainWhite,
                            fontFamily: scssVariables.fontFamily,
                        },
                        ...props.customStyles,
                    }}
                    MenuProps={{
                        // The menu focuses the selected item on open, which would pull focus straight
                        // back off the search box; let the search box keep it instead.
                        autoFocus: !props.isSearchable,
                        PaperProps: {
                            style: {
                                maxHeight: 400,
                            },
                        },
                    }}
                >
                    {props.isSearchable && (
                        <ListSubheader sx={{ padding: '8px', backgroundColor: scssVariables.plainWhite }}>
                            <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                placeholder={languageService.translate(translationKeys.actions.search)}
                                value={searchText}
                                onChange={(event) => setSearchText(event.target.value)}
                                // The Select steals key presses for its own type-ahead, which would move
                                // focus out of this field, so keep them local to the search box. Navigation
                                // keys still go through so the menu can be driven from the keyboard.
                                onKeyDown={(event) => {
                                    if (!menuNavigationKeys.includes(event.key)) {
                                        event.stopPropagation();
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </ListSubheader>
                    )}
                    {props.options
                        .filter((value, index, array) => array.findIndex((x) => x.label === value.label) === index)
                        .map((option) => (
                            <MenuItem
                                key={`${option.label}`}
                                value={`${option.label}`}
                                disabled={option.disabled}
                                // Keep non-matching options mounted but hidden. Removing them would make the
                                // current value out of range while the search is active, which blanks the field.
                                sx={[
                                    { fontFamily: scssVariables.fontFamily },
                                    { '&.Mui-selected': { backgroundColor: scssVariables.stonexSkyBlue } },
                                    { '&:hover': { backgroundColor: scssVariables.deactivatedBlockColor } },
                                    { '&.Mui-disabled': { opacity: 0.5, cursor: 'not-allowed' } },
                                    !!props.isSearchable && !`${option.label ?? ''}`.toLowerCase().includes(searchText.toLowerCase()) && { display: 'none' },
                                ]}
                                data-testid={option.label}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                </StyledMuiSelect>
            </FormControl>
        </div>
    );
};

export default Dropdown;
